/**
 * 顾问陪跑 · 销售账号（运营主管「招销售」，sales_account:manage；超管天然拥有）。
 *
 * 能做：建销售账号（立即生效）、看销售账号列表、绑 / 解绑企业微信成员、停用 / 启用。
 * 只能碰「销售」角色的账号——建不了、改不了任何别的角色（后端 /sales-accounts 强制，页面只是体验层）。
 * 角色管理、其它账号的停用 / 踢会话仍在「系统 → 管理员」（仅超管）。
 *
 * 招人流程：对方先用微信扫码登录后台 → 登录页会显示他的 unionId → 他发给你 → 你填在这里创建；创建后立即生效。
 * unionId 只在创建时填一次，列表与所有响应都不再回显它。
 *
 * 注意（项目踩过的坑）：antd 的 message.xxx() 返回 thenable，react-query 的 onSuccess / onError
 * 回调里必须写成块体且不 return，否则会等它 resolve，按钮 loading 会多挂约 3 秒。
 */
import { useState } from 'react';
import { Alert, Button, Card, Form, Input, Modal, Popconfirm, Result, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../../components/PermissionGate';
import { PERMISSIONS } from '../../../contracts/permissions';
import {
  createSalesAccount,
  disableSalesAccount,
  enableSalesAccount,
  listSalesAccounts,
  setSalesAccountWecom,
  type SalesAccountDto,
} from '../../../api/sales-accounts';

const { Text } = Typography;

/** 企微成员账号格式：与后端 parseWecomUserIdInput 同一个规则 */
export const WECOM_USERID_PATTERN = /^[A-Za-z0-9_.@-]{1,64}$/;

const UNION_ID_MAX_LENGTH = 128;
const NICKNAME_MAX_LENGTH = 64;
const DISABLE_REASON_MAX_LENGTH = 255;

const errText = (e: unknown, fallback: string): string => {
  const err = e as { response?: { data?: { message?: string } }; message?: string } | null;
  return err?.response?.data?.message ?? err?.message ?? fallback;
};

const fmtTime = (v: string | null): string => (v ? new Date(v).toLocaleString('zh-CN') : '-');

/** 绑定企微弹窗：自己持有表单实例，关闭即卸载，下次打开是干净的 */
function WecomBindModal(props: {
  target: SalesAccountDto;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (wecomUserId: string) => void;
}) {
  const { target, loading, onCancel, onSubmit } = props;
  const [form] = Form.useForm<{ wecomUserId?: string }>();
  return (
    <Modal
      open
      title={`${target.wecomBound ? '重新绑定' : '绑定'}企业微信 · ${target.nickname}`}
      okText="保存"
      cancelText="取消"
      confirmLoading={loading}
      onCancel={onCancel}
      onOk={() => {
        // 校验不通过时表单自己会标红，第二个参数吞掉 reject，避免 unhandled rejection
        form.validateFields().then(
          (v) => onSubmit((v.wecomUserId ?? '').trim()),
          () => undefined,
        );
      }}
    >
      <Form form={form} name="sales-account-wecom" layout="vertical">
        <Form.Item
          name="wecomUserId"
          label="企业微信成员账号"
          extra="企业微信通讯录里该成员的「账号」，如 ZhangSan。销售在企业微信聊天侧边栏打开「顾问助手」时，按这个账号识别是谁。"
          rules={[
            { required: true, whitespace: true, message: '请填写企业微信成员账号' },
            {
              validator: (_rule, value?: string) =>
                !value || WECOM_USERID_PATTERN.test(value.trim())
                  ? Promise.resolve()
                  : Promise.reject(new Error('格式不对：只能含字母、数字和 _ . @ -，最长 64 位')),
            },
          ]}
        >
          <Input placeholder="例：ZhangSan" maxLength={64} autoComplete="off" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/** 停用二次确认弹窗 */
function DisableModal(props: {
  target: SalesAccountDto;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
}) {
  const { target, loading, onCancel, onSubmit } = props;
  const [form] = Form.useForm<{ reason?: string }>();
  return (
    <Modal
      open
      title={`停用销售账号：${target.nickname}`}
      okText="确认停用"
      cancelText="取消"
      okButtonProps={{ danger: true }}
      confirmLoading={loading}
      onCancel={onCancel}
      onOk={() => {
        form.validateFields().then(
          (v) => onSubmit((v.reason ?? '').trim()),
          () => undefined,
        );
      }}
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="停用后该账号会立即被踢下线，无法再登录。名下客户需先转交，否则会没人跟进。"
        description={
          target.customerCount != null && target.customerCount > 0
            ? `这位销售名下目前还有 ${target.customerCount} 位客户，请确认已经转交。`
            : undefined
        }
      />
      <Form form={form} name="sales-account-disable" layout="vertical">
        <Form.Item name="reason" label="停用原因（选填，记入审计日志）">
          <Input.TextArea
            rows={2}
            maxLength={DISABLE_REASON_MAX_LENGTH}
            placeholder="例：离职 / 长期不跟进 / 试用期结束"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function SalesAccountsPageInner() {
  const qc = useQueryClient();
  const [createForm] = Form.useForm<{ unionId: string; nickname: string }>();
  const [wecomTarget, setWecomTarget] = useState<SalesAccountDto | null>(null);
  const [disableTarget, setDisableTarget] = useState<SalesAccountDto | null>(null);

  const listQuery = useQuery({ queryKey: ['sales-accounts'], queryFn: listSalesAccounts });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['sales-accounts'] });
  }

  const createMutation = useMutation({
    mutationFn: createSalesAccount,
    onSuccess: () => {
      message.success('销售账号已创建，立即生效');
      createForm.resetFields();
      invalidate();
    },
    onError: (e) => {
      message.error(errText(e, '创建失败'));
    },
  });

  const wecomMutation = useMutation({
    mutationFn: (vars: { id: string; wecomUserId: string | null }) =>
      setSalesAccountWecom(vars.id, vars.wecomUserId),
    onSuccess: (_data, vars) => {
      message.success(vars.wecomUserId ? '企业微信已绑定' : '企业微信已解绑');
      setWecomTarget(null);
      invalidate();
    },
    onError: (e) => {
      message.error(errText(e, '更新失败'));
    },
  });

  const disableMutation = useMutation({
    mutationFn: (vars: { id: string; reason: string }) => disableSalesAccount(vars.id, vars.reason || undefined),
    onSuccess: () => {
      message.success('账号已停用');
      setDisableTarget(null);
      invalidate();
    },
    onError: (e) => {
      message.error(errText(e, '停用失败'));
    },
  });

  const enableMutation = useMutation({
    mutationFn: enableSalesAccount,
    onSuccess: () => {
      message.success('账号已启用');
      invalidate();
    },
    onError: (e) => {
      message.error(errText(e, '启用失败'));
    },
  });

  function submitCreate() {
    // 校验不通过时表单自己会标红，第二个参数吞掉 reject，避免 unhandled rejection
    createForm.validateFields().then(
      (v) => createMutation.mutate({ unionId: v.unionId.trim(), nickname: v.nickname.trim() }),
      () => undefined,
    );
  }

  const quota = listQuery.data?.createQuota;

  const columns: ColumnsType<SalesAccountDto> = [
    {
      title: '昵称',
      dataIndex: 'nickname',
      key: 'nickname',
      width: 160,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (s: SalesAccountDto['status']) =>
        s === 'active' ? <Tag color="green">在职</Tag> : <Tag color="red">已停用</Tag>,
    },
    {
      title: '企业微信',
      key: 'wecom',
      width: 230,
      render: (_: unknown, row) => (
        <Space size={4} wrap>
          {row.wecomBound ? <Tag color="blue">已绑定</Tag> : <Tag color="orange">未绑定</Tag>}
          <Button
            size="small"
            type="link"
            onClick={() => setWecomTarget(row)}
            data-testid={`sales-wecom-${row.id}`}
          >
            {row.wecomBound ? '重新绑定' : '绑定'}
          </Button>
          {row.wecomBound && (
            <Popconfirm
              title="确认解绑该销售的企业微信？"
              description="解绑后他在企业微信侧边栏里无法再识别身份。"
              okText="解绑"
              cancelText="取消"
              onConfirm={() => wecomMutation.mutate({ id: row.id, wecomUserId: null })}
            >
              <Button size="small" type="link" danger data-testid={`sales-unbind-${row.id}`}>
                解绑
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
    {
      title: '创建人 / 时间',
      key: 'created',
      width: 190,
      render: (_: unknown, row) => (
        <Space direction="vertical" size={0}>
          <span>{row.createdByNickname ?? '-'}</span>
          <Text type="secondary">{fmtTime(row.createdAt)}</Text>
        </Space>
      ),
    },
    {
      title: '最近登录',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      width: 170,
      render: (v: string | null) => fmtTime(v),
    },
    {
      title: '名下客户',
      dataIndex: 'customerCount',
      key: 'customerCount',
      width: 90,
      render: (v: number | null) => (v == null ? '-' : v),
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_: unknown, row) =>
        row.status === 'active' ? (
          <Button size="small" danger onClick={() => setDisableTarget(row)} data-testid={`sales-disable-${row.id}`}>
            停用
          </Button>
        ) : (
          <Popconfirm
            title="确认启用该账号？"
            okText="启用"
            cancelText="取消"
            onConfirm={() => enableMutation.mutate(row.id)}
          >
            <Button size="small" data-testid={`sales-enable-${row.id}`}>
              启用
            </Button>
          </Popconfirm>
        ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }} data-testid="sales-accounts-page">
      <Card title="新建销售账号" size="small">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="对方先用微信扫码登录后台，登录页会显示其 unionId，发给你后填在这里；创建后立即生效。"
          description="这里只能创建「销售」角色的账号（只看自己名下客户）。unionId 创建后不再显示，请核对无误再提交。"
        />
        <Form form={createForm} name="sales-account-create" layout="inline" data-testid="sales-create-form">
          <Form.Item
            name="unionId"
            label="unionId"
            rules={[
              { required: true, whitespace: true, message: '请填写 unionId' },
              {
                validator: (_rule, value?: string) => {
                  const v = (value ?? '').trim();
                  if (!v) return Promise.resolve();
                  if (v.length > UNION_ID_MAX_LENGTH) {
                    return Promise.reject(new Error(`unionId 最长 ${UNION_ID_MAX_LENGTH} 位`));
                  }
                  return /\s/.test(v) ? Promise.reject(new Error('unionId 中间不能有空格')) : Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="对方发来的 unionId" style={{ width: 300 }} autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="nickname"
            label="昵称"
            rules={[
              { required: true, whitespace: true, message: '请填写昵称' },
              { max: NICKNAME_MAX_LENGTH, message: `昵称最长 ${NICKNAME_MAX_LENGTH} 个字符` },
            ]}
          >
            <Input placeholder="显示名，如：小王" style={{ width: 200 }} autoComplete="off" />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              onClick={submitCreate}
              loading={createMutation.isPending}
              data-testid="sales-create-submit"
            >
              创建销售账号
            </Button>
          </Form.Item>
        </Form>
        {quota && (
          <div style={{ marginTop: 8 }}>
            <Text type={quota.used >= quota.limit ? 'danger' : 'secondary'}>
              {quota.windowHours} 小时内最多可创建 {quota.limit} 个销售账号，你已创建 {quota.used} 个
              {quota.used >= quota.limit ? '（已达上限，再创建会被拒绝）' : ''}
            </Text>
          </div>
        )}
      </Card>

      <Card title="销售账号" size="small">
        <Table<SalesAccountDto>
          rowKey="id"
          columns={columns}
          dataSource={listQuery.data?.items ?? []}
          loading={listQuery.isLoading}
          pagination={{ pageSize: 50, showTotal: (t) => `共 ${t} 个销售账号` }}
          locale={{ emptyText: '还没有销售账号' }}
        />
      </Card>

      {wecomTarget && (
        <WecomBindModal
          target={wecomTarget}
          loading={wecomMutation.isPending}
          onCancel={() => setWecomTarget(null)}
          onSubmit={(wecomUserId) => wecomMutation.mutate({ id: wecomTarget.id, wecomUserId })}
        />
      )}
      {disableTarget && (
        <DisableModal
          target={disableTarget}
          loading={disableMutation.isPending}
          onCancel={() => setDisableTarget(null)}
          onSubmit={(reason) => disableMutation.mutate({ id: disableTarget.id, reason })}
        />
      )}
    </Space>
  );
}

export default function SalesAccountsPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.SALES_ACCOUNT_MANAGE}
      fallback={
        <Result
          status="403"
          title="无权访问"
          subTitle="销售账号管理仅运营主管与超管可见，请联系超管申请权限。"
        />
      }
    >
      <SalesAccountsPageInner />
    </PermissionGate>
  );
}
